import type {
    AppointmentStatus,
    ClinicSettings,
    FollowUpBucket,
    Gender,
    MessageKind,
    MessageStatus,
    MessageTemplateKey,
    PatientStatus,
    PaymentMethod,
    PaymentStatus,
    TreatmentInput
} from '@/types/clinic'

export type Tone = 'slate' | 'teal' | 'blue' | 'green' | 'amber' | 'red' | 'orange' | 'violet'

export interface StatusMeta {
    label: string
    tone: Tone
}

export const DATA_VERSION = 1

export const STORAGE_KEY = 'dr-sayuja-clinic:data'

export const APPOINTMENT_STATUSES: AppointmentStatus[] = [
    'scheduled',
    'confirmed',
    'arrived',
    'in-progress',
    'completed',
    'cancelled',
    'no-show'
]

// These three cannot be switched off in settings: every booking starts as
// scheduled and must be able to end as completed or cancelled.
export const REQUIRED_APPOINTMENT_STATUSES: AppointmentStatus[] = ['scheduled', 'completed', 'cancelled']

// Bookings in these states still occupy their time slot.
export const OPEN_APPOINTMENT_STATUSES: AppointmentStatus[] = ['scheduled', 'confirmed', 'arrived', 'in-progress']

export const APPOINTMENT_STATUS_META: Record<AppointmentStatus, StatusMeta & { description: string }> = {
    'scheduled': { label: 'Scheduled', tone: 'slate', description: 'Booked, waiting for confirmation' },
    'confirmed': { label: 'Confirmed', tone: 'blue', description: 'Patient confirmed the visit' },
    'arrived': { label: 'Arrived', tone: 'violet', description: 'Patient is at the clinic' },
    'in-progress': { label: 'In Progress', tone: 'amber', description: 'Treatment is under way' },
    'completed': { label: 'Completed', tone: 'green', description: 'Visit finished' },
    'cancelled': { label: 'Cancelled', tone: 'red', description: 'Booking was cancelled' },
    'no-show': { label: 'No Show', tone: 'orange', description: 'Patient did not turn up' }
}

export const PAYMENT_STATUS_META: Record<PaymentStatus, StatusMeta> = {
    paid: { label: 'Paid', tone: 'green' },
    partial: { label: 'Partially Paid', tone: 'amber' },
    pending: { label: 'Pending', tone: 'red' }
}

export const PAYMENT_STATUSES: PaymentStatus[] = ['paid', 'partial', 'pending']

export const PATIENT_STATUS_META: Record<PatientStatus, StatusMeta> = {
    'active': { label: 'Active', tone: 'teal' },
    'in-treatment': { label: 'Under Treatment', tone: 'blue' },
    'completed': { label: 'Treatment Completed', tone: 'green' },
    'inactive': { label: 'Inactive', tone: 'slate' }
}

export const PATIENT_STATUSES: PatientStatus[] = ['active', 'in-treatment', 'completed', 'inactive']

export const FOLLOW_UP_BUCKET_META: Record<FollowUpBucket, StatusMeta> = {
    overdue: { label: 'Overdue', tone: 'red' },
    today: { label: 'Due Today', tone: 'teal' },
    tomorrow: { label: 'Tomorrow', tone: 'blue' },
    upcoming: { label: 'Upcoming', tone: 'slate' },
    completed: { label: 'Completed', tone: 'green' },
    cancelled: { label: 'Cancelled', tone: 'slate' }
}

export const PAYMENT_METHODS: PaymentMethod[] = ['cash', 'card', 'upi', 'bank-transfer', 'other']

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
    'cash': 'Cash',
    'card': 'Card',
    'upi': 'UPI',
    'bank-transfer': 'Bank Transfer',
    'other': 'Other'
}

export const GENDER_LABELS: Record<Gender, string> = {
    male: 'Male',
    female: 'Female',
    other: 'Other'
}

export const MESSAGE_TEMPLATE_KEYS: MessageTemplateKey[] = [
    'appointmentConfirmation',
    'appointmentReminder',
    'followUpReminder',
    'paymentReminder'
]

export const MESSAGE_KIND_LABELS: Record<MessageKind, string> = {
    appointmentConfirmation: 'Appointment confirmation',
    appointmentReminder: 'Appointment reminder',
    followUpReminder: 'Returning patient reminder',
    paymentReminder: 'Payment reminder',
    custom: 'Custom message'
}

export const MESSAGE_STATUS_META: Record<MessageStatus, StatusMeta> = {
    draft: { label: 'Draft', tone: 'slate' },
    ready: { label: 'Ready', tone: 'blue' },
    sent: { label: 'Sent (demo)', tone: 'green' }
}

export const TEMPLATE_PLACEHOLDERS = [
    '[Patient Name]',
    '[Date]',
    '[Time]',
    '[Amount]',
    '[Treatment]',
    '[Clinic Name]',
    '[Clinic Phone]'
]

export const DEFAULT_TEMPLATES: Record<MessageTemplateKey, string> = {
    appointmentConfirmation: 'Hello [Patient Name], your appointment at [Clinic Name] has been scheduled for [Date] at [Time]. We look forward to seeing you.',
    appointmentReminder: 'Hello [Patient Name], this is a reminder of your dental appointment at [Clinic Name] on [Date] at [Time].',
    followUpReminder: 'Hello [Patient Name], we hope you are doing well. This is a friendly reminder that your next dental visit is due on [Date]. Please contact [Clinic Name] to confirm your appointment.',
    paymentReminder: 'Hello [Patient Name], this is a gentle reminder regarding your pending payment of [Amount] at [Clinic Name].'
}

export const WEEKDAYS: Array<{ value: number, label: string, short: string }> = [
    { value: 1, label: 'Monday', short: 'Mon' },
    { value: 2, label: 'Tuesday', short: 'Tue' },
    { value: 3, label: 'Wednesday', short: 'Wed' },
    { value: 4, label: 'Thursday', short: 'Thu' },
    { value: 5, label: 'Friday', short: 'Fri' },
    { value: 6, label: 'Saturday', short: 'Sat' },
    { value: 0, label: 'Sunday', short: 'Sun' }
]

export const CURRENCY_OPTIONS: Array<{ value: string, label: string }> = [
    { value: 'INR', label: 'Indian Rupee (₹)' },
    { value: 'AED', label: 'UAE Dirham (AED)' },
    { value: 'SAR', label: 'Saudi Riyal (SAR)' },
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'EUR', label: 'Euro (€)' }
]

export const APPOINTMENT_DURATIONS = [15, 20, 30, 45, 60, 90, 120]

export const DEFAULT_SETTINGS: ClinicSettings = {
    clinic: {
        name: 'Dr. Sayuja Clinic',
        tagline: 'Advanced Dental Care',
        logo: '',
        address: 'First Floor, Green Plaza, Mavoor Road, Kozhikode, Kerala 673004',
        phone: '+91 98470 23456',
        email: 'care@drsayujaclinic.in',
        website: '',
        doctorName: 'Dr. Sayuja',
        doctorQualification: 'BDS, MDS (Conservative Dentistry & Endodontics)',
        registrationNumber: 'Reg. No. KSDC 4521',
        prescriptionFooter: 'Clinic hours: Monday to Saturday, 9:30 AM to 7:00 PM. For dental emergencies, call the clinic.',
        invoiceFooter: 'Thank you for choosing Dr. Sayuja Clinic. This is a computer generated invoice.'
    },
    appointments: {
        workingDays: [1, 2, 3, 4, 5, 6],
        openTime: '09:30',
        closeTime: '19:00',
        breakStart: '13:00',
        breakEnd: '14:00',
        slotDuration: 30,
        enabledStatuses: [...APPOINTMENT_STATUSES]
    },
    prescription: {
        defaultInstructions: 'Take medicines after food unless advised otherwise.\nAvoid chewing on the treated side for 24 hours.\nRinse gently with warm salt water after meals.\nContact the clinic if you notice swelling, bleeding or severe pain.',
        paperSize: 'A4',
        showLogo: true,
        showSignature: true,
        showFooter: true
    },
    billing: {
        currency: 'INR',
        invoicePrefix: 'INV',
        taxEnabled: false,
        taxLabel: 'GST',
        taxRate: 18,
        paymentMethods: [...PAYMENT_METHODS]
    },
    whatsapp: {
        countryCode: '91',
        templates: { ...DEFAULT_TEMPLATES },
        phoneNumberId: '',
        businessAccountId: ''
    }
}

export const DEFAULT_TREATMENTS: Array<TreatmentInput & { key: string }> = [
    { key: 'scaling', name: 'Teeth Cleaning / Scaling', price: 1200, duration: 30, active: true, description: 'Ultrasonic scaling and polishing to remove plaque, tartar and stains.' },
    { key: 'rct', name: 'Root Canal Treatment (RCT)', price: 4500, duration: 60, active: true, description: 'Cleaning, shaping and sealing of an infected root canal, usually over two sittings.' },
    { key: 'extraction', name: 'Tooth Extraction', price: 800, duration: 30, active: true, description: 'Removal of a non-restorable, mobile or impacted tooth under local anaesthesia.' },
    { key: 'filling', name: 'Dental Filling', price: 1000, duration: 30, active: true, description: 'Tooth coloured composite restoration for decayed or chipped teeth.' },
    { key: 'consultation', name: 'Tooth Pain Consultation', price: 300, duration: 20, active: true, description: 'Examination, diagnosis and treatment plan for tooth pain or swelling.' },
    { key: 'crown', name: 'Dental Crown', price: 6000, duration: 45, active: true, description: 'Zirconia or metal ceramic cap to protect a weakened or root treated tooth.' },
    { key: 'bridge', name: 'Dental Bridge', price: 15000, duration: 60, active: true, description: 'Fixed three unit bridge to replace a missing tooth.' },
    { key: 'whitening', name: 'Teeth Whitening', price: 8000, duration: 60, active: true, description: 'In-office bleaching to lighten tooth shade by several steps.' },
    { key: 'denture', name: 'Denture', price: 12000, duration: 45, active: true, description: 'Complete or partial removable denture, including trial and fitting.' },
    { key: 'gum', name: 'Gum Treatment', price: 2500, duration: 45, active: true, description: 'Deep scaling and root planing for gum disease, per arch.' },
    { key: 'checkup', name: 'Dental Checkup', price: 200, duration: 15, active: true, description: 'Routine oral examination and oral hygiene advice.' },
    { key: 'other', name: 'Other', price: 500, duration: 30, active: true, description: 'Minor procedures and custom charges. Edit the amount on each bill.' }
]

export const MEDICINE_SUGGESTIONS = [
    'Amoxicillin 500 mg',
    'Amoxicillin + Clavulanic Acid 625 mg',
    'Metronidazole 400 mg',
    'Clindamycin 300 mg',
    'Azithromycin 500 mg',
    'Ibuprofen 400 mg',
    'Paracetamol 650 mg',
    'Aceclofenac 100 mg + Paracetamol 325 mg',
    'Ketorolac 10 mg',
    'Pantoprazole 40 mg',
    'Chlorhexidine Mouthwash 0.2%',
    'Povidone Iodine Gargle 2%',
    'Desensitising Toothpaste (Potassium Nitrate 5%)',
    'Benzocaine Oral Gel 20%'
]

export const DOSAGE_SUGGESTIONS = ['1 tablet', '1 capsule', '1/2 tablet', '10 ml', '15 ml', 'Pea sized amount', 'Apply thin layer']

export const FREQUENCY_SUGGESTIONS = [
    '1-0-1 (Twice daily)',
    '1-1-1 (Three times daily)',
    '1-0-0 (Morning)',
    '0-0-1 (Night)',
    'SOS (Only if needed)',
    'Twice daily',
    'Three times daily',
    'Once daily'
]

export const DURATION_SUGGESTIONS = ['3 days', '5 days', '7 days', '10 days', '2 weeks', '4 weeks', 'Until review']

export const INSTRUCTION_SUGGESTIONS = ['After food', 'Before food', 'Before breakfast', 'At bedtime', 'Rinse for 30 seconds, do not swallow', 'Avoid alcohol', 'Brush twice daily']
